/**
 * backfillTimeline — the segment model behind the backfill wizard's timeline.
 *
 * Everything here is pure and total: no React, no DOM, no clock reads. That is
 * deliberate and follows the house precedent set by `hooks/useNavOverflow.ts` —
 * jsdom reports zero for every layout box, so geometry and time math that lives
 * inside a component is effectively untestable. Keep it here, test it directly.
 *
 * The wizard used to hold a stack: a `blocks` array plus a parallel `decisions`
 * prefix array whose *length* was the cursor. That made random access
 * structurally impossible — index and cursor were the same number. This module
 * replaces it with segments carrying stable ids, so any segment can be selected
 * and edited at any time, in any order.
 *
 * Canonical storage order is **ascending by start** (chronological). The walk
 * still presents newest-first; that is a view concern, see `walkOrder`.
 *
 * Edge drags borrow the video-editing vocabulary:
 *   - ROLL   pins the neighbour's outer edge and absorbs the change by
 *            shrinking it. Nothing else on the timeline moves.
 *   - RIPPLE preserves the neighbour's duration and slides it, propagating
 *            outward until some gap absorbs the remainder.
 *
 * Two rules that are specific to *time tracking* rather than video, because
 * here the holes in the timeline carry meaning:
 *
 *   1. **Unaccounted slack absorbs ripple.** A ripple spends the gaps it meets
 *      and stops as soon as one swallows the remainder, so extending a block
 *      into an empty stretch never slides the rest of the morning.
 *   2. **Ripple pushes, it never pulls.** Shrinking a segment leaves a gap
 *      rather than dragging neighbours in to close it. The gap *is* the signal
 *      ("this time is still unaccounted"); closing it automatically would
 *      destroy the very thing the wizard exists to surface.
 */

/** Segments shorter than this are not worth a stop in the walk. */
export const MIN_BLOCK_MS = 5 * 60000;

export interface BlockFields {
  description: string;
  project: string;
  task: string;
  tags: string;
}

export const emptyFields: BlockFields = {
  description: '',
  project: '',
  task: '',
  tags: '',
};

/** A half-open interval. Used for tracked entries and computed windows. */
export interface Interval {
  start: Date;
  end: Date;
}

export type SegmentState = 'pending' | 'filled' | 'skipped';
export type EditMode = 'roll' | 'ripple';
export type Edge = 'start' | 'end';

export interface Segment extends Interval {
  id: string;
  state: SegmentState;
  fields: BlockFields;
  /**
   * Set when a ripple relocated this segment rather than the owner placing it.
   * Duration is preserved but the *placement* is an inference, so the UI marks
   * it — a large gesture must not quietly rewrite already-annotated time.
   */
  movedByRipple?: boolean;
}

export interface ResizeOptions {
  /** Floor on any segment's duration. Defaults to `MIN_BLOCK_MS`. */
  minDurationMs?: number;
  /** Hard clamp: nothing may start before this (local day start). */
  lowerBound?: Date | null;
  /** Hard clamp: nothing may end after this (min of `now` and day end). */
  upperBound?: Date | null;
}

const ms = (d: Date): number => d.getTime();
const at = (value: number): Date => new Date(value);

let idCounter = 0;
/**
 * Segment ids are local to one wizard session and never leave the browser —
 * `BulkTimeEntryItem` is `extra="forbid"`, so an id on the wire is a 422.
 * A counter (not a random source) keeps this module pure and its tests stable.
 */
export const nextSegmentId = (): string => {
  idCounter += 1;
  return `seg-${idCounter}`;
};

/** Test seam: reset the id counter so ids are predictable per test. */
export const resetSegmentIds = (): void => {
  idCounter = 0;
};

export const durationMs = (segment: Interval): number =>
  ms(segment.end) - ms(segment.start);

// --------------------------------------------------------------------------
// Deriving the initial segments
// --------------------------------------------------------------------------

/**
 * Merge the tracked intervals, then subtract them from `[anchor, boundary]`.
 *
 * Moved here verbatim from the wizard: behaviour is unchanged, and the walk
 * still needs it to decide what is genuinely unaccounted.
 */
export const unaccountedWindows = (
  anchor: Date,
  boundary: Date,
  tracked: Interval[],
): Interval[] => {
  const intervals = tracked
    .map((iv) => ({ start: ms(iv.start), end: ms(iv.end) }))
    .filter((iv) => Number.isFinite(iv.start) && Number.isFinite(iv.end) && iv.end > iv.start)
    .sort((a, b) => a.start - b.start);

  const merged: { start: number; end: number }[] = [];
  for (const iv of intervals) {
    const last = merged[merged.length - 1];
    if (last && iv.start <= last.end) last.end = Math.max(last.end, iv.end);
    else merged.push({ ...iv });
  }

  const windows: Interval[] = [];
  let cursor = ms(anchor);
  const limit = ms(boundary);
  for (const iv of merged) {
    if (iv.end <= cursor) continue;
    if (iv.start >= limit) break;
    if (iv.start > cursor) {
      windows.push({ start: at(cursor), end: at(Math.min(iv.start, limit)) });
    }
    cursor = Math.max(cursor, iv.end);
    if (cursor >= limit) break;
  }
  if (cursor < limit) windows.push({ start: at(cursor), end: at(limit) });
  return windows;
};

/** Slice windows into blocks from each window's END backwards, newest first. */
export const sliceBackwards = (windows: Interval[], blockMinutes: number): Interval[] => {
  const sizeMs = blockMinutes * 60000;
  const blocks: Interval[] = [];
  const newestFirst = [...windows].sort((a, b) => ms(b.end) - ms(a.end));
  for (const window of newestFirst) {
    let end = ms(window.end);
    const floor = ms(window.start);
    while (end - floor >= MIN_BLOCK_MS) {
      const start = Math.max(floor, end - sizeMs);
      blocks.push({ start: at(start), end: at(end) });
      end = start;
    }
  }
  return blocks;
};

/**
 * Build the initial segment list. Stored ascending; the walk order is derived.
 *
 * The block size chosen at setup is only an opening proposal — every boundary
 * is draggable afterwards, so this is a starting grid rather than a commitment.
 */
export const buildSegments = (windows: Interval[], blockMinutes: number): Segment[] =>
  sliceBackwards(windows, blockMinutes)
    .sort((a, b) => ms(a.start) - ms(b.start))
    .map((block) => ({
      id: nextSegmentId(),
      start: block.start,
      end: block.end,
      state: 'pending' as SegmentState,
      fields: { ...emptyFields },
    }));

/**
 * Indices in walk order — most recent first, because memory works backwards
 * from the last thing you did.
 */
export const walkOrder = (segments: Segment[]): string[] =>
  [...segments].sort((a, b) => ms(b.end) - ms(a.end)).map((segment) => segment.id);

// --------------------------------------------------------------------------
// Resizing
// --------------------------------------------------------------------------

const clone = (segment: Segment): Segment => ({ ...segment });

/**
 * Total room available when pushing `index` outward in RIPPLE: every gap on
 * that side, plus the run-out from the outermost segment to the hard bound.
 * Durations are preserved, so this is exactly the free space, no more.
 */
const rippleCapacity = (
  segments: Segment[],
  index: number,
  edge: Edge,
  boundMs: number,
): number => {
  let capacity = 0;
  if (edge === 'start') {
    let frontier = ms(segments[index].start);
    for (let i = index - 1; i >= 0; i -= 1) {
      capacity += frontier - ms(segments[i].end);
      frontier = ms(segments[i].start);
    }
    return capacity + (frontier - boundMs);
  }
  let frontier = ms(segments[index].end);
  for (let i = index + 1; i < segments.length; i += 1) {
    capacity += ms(segments[i].start) - frontier;
    frontier = ms(segments[i].end);
  }
  return capacity + (boundMs - frontier);
};

/**
 * Room available in ROLL: the adjacent gap, plus whatever the immediate
 * neighbour can give up before hitting the minimum duration. Only that one
 * neighbour ever participates — that is what makes roll "local".
 */
const rollCapacity = (
  segments: Segment[],
  index: number,
  edge: Edge,
  boundMs: number,
  minDuration: number,
): number => {
  const neighbour = edge === 'start' ? segments[index - 1] : segments[index + 1];
  if (!neighbour) {
    return edge === 'start' ? ms(segments[index].start) - boundMs : boundMs - ms(segments[index].end);
  }
  const gap =
    edge === 'start'
      ? ms(segments[index].start) - ms(neighbour.end)
      : ms(neighbour.start) - ms(segments[index].end);
  return gap + Math.max(0, durationMs(neighbour) - minDuration);
};

/**
 * Move one edge of one segment to `targetTime`, propagating per `mode`.
 *
 * Returns a new array (input untouched). The gesture is **clamped, never
 * partially applied**: if the requested time is unreachable — a neighbour would
 * be crushed below the minimum, or a hard bound is in the way — the edge lands
 * as close as the constraints allow rather than deleting anything.
 *
 * Shrinking never propagates in either mode; see the header note on why gaps
 * are left alone.
 */
export const resizeSegment = (
  segments: Segment[],
  id: string,
  edge: Edge,
  targetTime: Date,
  mode: EditMode,
  options: ResizeOptions = {},
): Segment[] => {
  const index = segments.findIndex((segment) => segment.id === id);
  if (index === -1) return segments;

  const minDuration = options.minDurationMs ?? MIN_BLOCK_MS;
  const next = segments.map(clone);
  const target = next[index];
  const targetMs = ms(targetTime);

  if (edge === 'start') {
    const lowerMs = options.lowerBound ? ms(options.lowerBound) : Number.NEGATIVE_INFINITY;
    // Shrinking: the edge moves right. Clamp at the minimum duration, leave a
    // gap behind, touch nothing else.
    if (targetMs >= ms(target.start)) {
      const latest = ms(target.end) - minDuration;
      target.start = at(Math.min(targetMs, latest));
      return target.start >= target.end ? segments : next;
    }
    // Growing leftward.
    const requested = ms(target.start) - targetMs;
    const capacity =
      mode === 'ripple'
        ? rippleCapacity(next, index, 'start', lowerMs)
        : rollCapacity(next, index, 'start', lowerMs, minDuration);
    const delta = Math.max(0, Math.min(requested, capacity));
    if (delta === 0) return segments;
    const newStart = ms(target.start) - delta;
    target.start = at(newStart);

    if (mode === 'roll') {
      const neighbour = next[index - 1];
      if (neighbour && ms(neighbour.end) > newStart) {
        neighbour.end = at(newStart);
      }
      return next;
    }
    // Ripple: push left only as far as necessary. The first neighbour that
    // already fits ends the chain — everything beyond it fits too.
    let limit = newStart;
    for (let i = index - 1; i >= 0; i -= 1) {
      if (ms(next[i].end) <= limit) break;
      const shift = ms(next[i].end) - limit;
      next[i].start = at(ms(next[i].start) - shift);
      next[i].end = at(ms(next[i].end) - shift);
      next[i].movedByRipple = true;
      limit = ms(next[i].start);
    }
    return next;
  }

  const upperMs = options.upperBound ? ms(options.upperBound) : Number.POSITIVE_INFINITY;
  if (targetMs <= ms(target.end)) {
    const earliest = ms(target.start) + minDuration;
    target.end = at(Math.max(targetMs, earliest));
    return target.end <= target.start ? segments : next;
  }
  const requested = targetMs - ms(target.end);
  const capacity =
    mode === 'ripple'
      ? rippleCapacity(next, index, 'end', upperMs)
      : rollCapacity(next, index, 'end', upperMs, minDuration);
  const delta = Math.max(0, Math.min(requested, capacity));
  if (delta === 0) return segments;
  const newEnd = ms(target.end) + delta;
  target.end = at(newEnd);

  if (mode === 'roll') {
    const neighbour = next[index + 1];
    if (neighbour && ms(neighbour.start) < newEnd) {
      neighbour.start = at(newEnd);
    }
    return next;
  }
  let limit = newEnd;
  for (let i = index + 1; i < next.length; i += 1) {
    if (ms(next[i].start) >= limit) break;
    const shift = limit - ms(next[i].start);
    next[i].start = at(ms(next[i].start) + shift);
    next[i].end = at(ms(next[i].end) + shift);
    next[i].movedByRipple = true;
    limit = ms(next[i].end);
  }
  return next;
};

// --------------------------------------------------------------------------
// Split / merge
// --------------------------------------------------------------------------

/**
 * Split a segment in two at `atTime`, or at its midpoint (rounded to the
 * minute) when no time is given. Both halves inherit the fields and state.
 */
export const splitSegment = (
  segments: Segment[],
  id: string,
  atTime?: Date,
  minDurationMs: number = MIN_BLOCK_MS,
): Segment[] => {
  const index = segments.findIndex((segment) => segment.id === id);
  if (index === -1) return segments;
  const target = segments[index];
  const span = durationMs(target);
  if (span < 2 * minDurationMs) return segments;

  const midpoint = atTime
    ? ms(atTime)
    : Math.round((ms(target.start) + span / 2) / 60000) * 60000;
  if (
    midpoint - ms(target.start) < minDurationMs ||
    ms(target.end) - midpoint < minDurationMs
  ) {
    return segments;
  }

  const earlier: Segment = {
    ...target,
    id: nextSegmentId(),
    end: at(midpoint),
    fields: { ...target.fields },
  };
  const later: Segment = {
    ...target,
    id: nextSegmentId(),
    start: at(midpoint),
    fields: { ...target.fields },
  };
  return [...segments.slice(0, index), earlier, later, ...segments.slice(index + 1)];
};

/**
 * Merge a segment into its neighbour, which swallows its span.
 *
 * Adjacency is strict (`end === start`), mirroring the old `canMerge`: a merge
 * must never reach across tracked time or a skipped break and silently claim
 * it. Merging stays an explicit action — adjacent segments that happen to carry
 * identical fields are NOT auto-coalesced, because two separate sessions on the
 * same project are legitimately two entries.
 */
export const mergeSegments = (
  segments: Segment[],
  id: string,
  into: 'earlier' | 'later',
): Segment[] => {
  const index = segments.findIndex((segment) => segment.id === id);
  if (index === -1) return segments;
  const neighbourIndex = into === 'earlier' ? index - 1 : index + 1;
  const neighbour = segments[neighbourIndex];
  const target = segments[index];
  if (!neighbour || neighbour.state === 'skipped' || target.state === 'skipped') {
    return segments;
  }
  const adjacent =
    into === 'earlier'
      ? ms(neighbour.end) === ms(target.start)
      : ms(neighbour.start) === ms(target.end);
  if (!adjacent) return segments;

  const merged: Segment = {
    ...neighbour,
    start: into === 'earlier' ? neighbour.start : target.start,
    end: into === 'earlier' ? target.end : neighbour.end,
  };
  return segments
    .filter((_, i) => i !== index && i !== neighbourIndex)
    .concat(merged)
    .sort((a, b) => ms(a.start) - ms(b.start));
};

/** True when `id` may merge in that direction — drives the button's disabled state. */
export const canMerge = (
  segments: Segment[],
  id: string,
  into: 'earlier' | 'later',
): boolean => mergeSegments(segments, id, into) !== segments;

// --------------------------------------------------------------------------
// Constraints and validation
// --------------------------------------------------------------------------

/**
 * Pull every segment inside the hard bounds and the minimum duration, dropping
 * any that cannot survive. A safety net for edited review rows, not the main
 * path — `resizeSegment` clamps as it goes.
 */
export const clampToConstraints = (
  segments: Segment[],
  options: ResizeOptions = {},
): Segment[] => {
  const minDuration = options.minDurationMs ?? MIN_BLOCK_MS;
  const lowerMs = options.lowerBound ? ms(options.lowerBound) : Number.NEGATIVE_INFINITY;
  const upperMs = options.upperBound ? ms(options.upperBound) : Number.POSITIVE_INFINITY;
  return segments
    .map((segment) => {
      const start = Math.max(ms(segment.start), lowerMs);
      const end = Math.min(ms(segment.end), upperMs);
      return { ...segment, start: at(start), end: at(end) };
    })
    .filter((segment) => durationMs(segment) >= minDuration)
    .sort((a, b) => ms(a.start) - ms(b.start));
};

/**
 * Overlapping pairs *within* the proposal. `resizeSegment` preserves the
 * non-overlapping invariant by construction, but the review step's free-text
 * time fields do not, so the commit path checks before sending.
 *
 * Touching intervals are not overlaps — same rule the server applies.
 */
export const findInternalOverlaps = (segments: Segment[]): [Segment, Segment][] => {
  const ordered = [...segments].sort((a, b) => ms(a.start) - ms(b.start));
  const pairs: [Segment, Segment][] = [];
  for (let i = 0; i < ordered.length; i += 1) {
    for (let j = i + 1; j < ordered.length; j += 1) {
      if (ms(ordered[j].start) >= ms(ordered[i].end)) break;
      if (ms(ordered[i].start) < ms(ordered[j].end)) {
        pairs.push([ordered[i], ordered[j]]);
      }
    }
  }
  return pairs;
};

/** The segments that will actually be committed, chronologically ascending. */
export const proposedSegments = (segments: Segment[]): Segment[] =>
  segments
    .filter((segment) => segment.state === 'filled')
    .sort((a, b) => ms(a.start) - ms(b.start));

// --------------------------------------------------------------------------
// Geometry — px <-> time. Pure so drag math is testable without layout.
// --------------------------------------------------------------------------

export interface Viewport {
  start: Date;
  end: Date;
  width: number;
}

export const timeToX = (time: Date, view: Viewport): number => {
  const span = ms(view.end) - ms(view.start);
  if (span <= 0 || view.width <= 0) return 0;
  return ((ms(time) - ms(view.start)) / span) * view.width;
};

export const xToTime = (x: number, view: Viewport): Date => {
  const span = ms(view.end) - ms(view.start);
  if (span <= 0 || view.width <= 0) return view.start;
  return at(ms(view.start) + (x / view.width) * span);
};

/** Width in px of an interval, floored at zero so a hairline never inverts. */
export const intervalWidth = (interval: Interval, view: Viewport): number =>
  Math.max(0, timeToX(interval.end, view) - timeToX(interval.start, view));

/**
 * Snap a dragged time to the grid, or to a nearby magnet (an existing entry's
 * edge) when one is within `thresholdMs`. Magnets win over the grid: lining up
 * exactly with a tracked entry is more useful than landing on a round number.
 */
export const snapTime = (
  time: Date,
  snapMinutes: number,
  magnets: Date[] = [],
  thresholdMs = 4 * 60000,
): Date => {
  let best: number | null = null;
  let bestDistance = thresholdMs;
  for (const magnet of magnets) {
    const distance = Math.abs(ms(magnet) - ms(time));
    if (distance <= bestDistance) {
      best = ms(magnet);
      bestDistance = distance;
    }
  }
  if (best !== null) return at(best);
  const grid = Math.max(1, snapMinutes) * 60000;
  return at(Math.round(ms(time) / grid) * grid);
};

/** Every edge of every tracked entry — the magnet set for `snapTime`. */
export const magnetsFrom = (tracked: Interval[]): Date[] =>
  tracked.flatMap((iv) => [iv.start, iv.end]);
